import { PAGE_BREAKPOINTS, UOM } from '@app/constants/commonConstants';
import { useBreakpoint } from '@app/hooks';
import { Grid } from 'cat-ecommerce-alloy';
import ProductCard from '../product/ProductCard';
import cx from 'classnames';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import { buildIntermediateUrl } from '@app/components/pages/plp/ProductListComparisonGrid/utils';
import styles from './styles.module.scss';
import PropTypes from 'prop-types';
import { getPartName } from '@app/utils/commonUtils';
import { filterSpecificationProductCardsByUom } from '@app/utils/products';

const ProductComparisonGrid = ({
  productsArray,
  renderAdditionalRowContent,
  pageSize,
  lowestLoadedPage,
  currentCategory,
  getFitmentCheck,
  getFitmentResult,
  priceData,
  shouldShowFitmentData,
  didFitmentCallResolve,
  partsWithFitmentData,
  isCanonicalUrl,
  priceAndAvailibiltyStatus,
  domain,
  path,
  handleSetExpanded,
  productCardExpandedArr,
  showLeftNav,
  locationAtt,
  gaListName,
  unitOfMeasure,
  facets
}) => {
  const isXXL = useBreakpoint(PAGE_BREAKPOINTS.XXL);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const withLeftNav = isXXL ? 4 : isLG ? 3 : isMD ? 2 : 1;
  const withoutLeftNav = isXXL ? 5 : isLG ? 4 : isMD ? 3 : 1;
  const cols = showLeftNav ? withLeftNav : withoutLeftNav;
  const { hash, search } = useLocation();
  const { url } = useRouteMatch();
  const history = useHistory();
  const parsedHash = queryString.parse(hash);
  const { scrollToProduct: productScrollId } = queryString.parse(search);

  const shouldScrollIntoView = card =>
    card.itemPartNumber === parsedHash.scrollToProduct ||
    card.itemPartNumber === productScrollId;
  const handleProductClick = (
    { itemPartNumber, productDisplayUrl },
    dataPage
  ) => {
    if (isCanonicalUrl) {
      const intermediateUrl = buildIntermediateUrl(
        itemPartNumber,
        dataPage,
        url,
        locationAtt
      );
      const updatedSearch = {
        ...queryString.parse(search, { decode: false }),
        scrollToProduct: itemPartNumber
      };
      const searchurl = `${url}?${queryString.stringify(updatedSearch, {
        encode: false
      })}`;

      history.push(locationAtt === 'search' ? searchurl : intermediateUrl);
    }
    window.location.href = productDisplayUrl;
  };

  const areDependentFacets = facets?.some(
    facet => facet.uom === UOM.us || facet.uom === UOM.metric
  );
  const dependentUnitOfMeasure = areDependentFacets ? unitOfMeasure : null;

  const updatedProductsArray = productsArray?.map(
    ({
      name,
      itemPartNumber,
      partNumber,
      productDisplayUrl,
      productURL,
      attributes = [],
      ...attrs
    }) => ({
      ...attrs,
      name: getPartName(itemPartNumber ?? partNumber, name),
      itemPartNumber: itemPartNumber ?? partNumber,
      productDisplayUrl: productDisplayUrl ?? productURL,
      attributes: unitOfMeasure
        ? filterSpecificationProductCardsByUom(
            attributes,
            dependentUnitOfMeasure
          )
        : attributes
    })
  );

  return (
    <section className="mb-4 mb-md-5">
      <Grid
        items={updatedProductsArray}
        cols={cols}
        className={cx(styles['comparison-grid'])}
        renderAdditionalRowContent={renderAdditionalRowContent}
        data-testid="comparison-grid"
      >
        {(item, index) => {
          const dataPage =
            Math.ceil((index + 1) / +pageSize) + lowestLoadedPage - 1;
          return (
            <ProductCard
              currentCategory={currentCategory}
              didFitmentCallResolve={
                didFitmentCallResolve ||
                partsWithFitmentData?.[item.itemPartNumber]
              }
              dataPage={dataPage}
              errorInfo={{
                domain,
                path
              }}
              expanded={productCardExpandedArr?.some(
                partNumber => partNumber === item?.itemPartNumber
              )}
              getFitmentCheck={getFitmentCheck}
              getFitmentResult={getFitmentResult}
              handleProductClick={() => handleProductClick(item, dataPage)}
              index={index}
              key={item?.itemPartNumber}
              preventProductReload={true}
              priceCallStatus={priceAndAvailibiltyStatus}
              priceData={priceData}
              scrollIntoView={shouldScrollIntoView(item)}
              setExpanded={() => handleSetExpanded(item?.itemPartNumber)}
              shouldShowFitmentData={shouldShowFitmentData}
              showRowVariant={!isMD}
              gaListName={gaListName}
              variant="comparison"
              {...item}
            />
          );
        }}
      </Grid>
    </section>
  );
};
ProductComparisonGrid.propTypes = {
  renderAdditionalRowContent: PropTypes.func,
  pageSize: PropTypes.number,
  isCanonicalUrl: PropTypes.bool,
  priceAndAvailibiltyStatus: PropTypes.string,
  domain: PropTypes.string,
  path: PropTypes.string,
  partsWithFitmentData: PropTypes.bool,
  shouldShowFitmentData: PropTypes.bool,
  locationAtt: PropTypes.string,
  lowestLoadedPage: PropTypes.number,
  currentCategory: PropTypes.string,
  getFitmentCheck: PropTypes.func,
  getFitmentResult: PropTypes.func,
  didFitmentCallResolve: PropTypes.string,
  expandedDispatch: PropTypes.func,
  gaListName: PropTypes.string,
  productCardExpandedArr: PropTypes.arrayOf(PropTypes.string),
  unitOfMeasure: PropTypes.string,
  facets: PropTypes.arrayOf(
    PropTypes.shape({
      allValuesReturned: PropTypes.bool,
      allowMultipleValueSelection: PropTypes.bool,
      facetId: PropTypes.string,
      label: PropTypes.string,
      uom: PropTypes.string,
      value: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          facet: PropTypes.string,
          label: PropTypes.string
        })
      )
    })
  )
};
export default ProductComparisonGrid;
